import { useEffect } from 'react';
import { useState } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  backlinkTabRoutes,
  buildBacklinkUrl
} from './utils/backlinkRoutes';
import ToolsBreadcrumb from '../common/components/ToolsBreadcrumb';
import ToolTabs from '@/app/common/components/ToolTabs';
import useDomain from '@/hooks/useDomain';
import NoDataBacklink from './pages/NoDataBacklink';
import { useDispatch, useSelector } from 'react-redux';
import { convertToQueryString, parseQueryString } from '@/utils/queryString';
import { setFilters } from '../common/reducers/filterSlice';
import { setDomain } from '../common/reducers/domainSlice';
import useBacklinkQueryBody from './hooks/useBacklinkQueryBody';
import { useGetDomainStatusQuery } from './api/backlinkDomainStatusApi';
import BacklinkPipelineState from './components/BacklinkPipelineState';
import _ from 'lodash';
import DataSourceNotice from '../common/components/DataSourceNotice';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError,
  type ApiQueryError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import ExpireCheck from '@/app/common/components/ExpireCheck';

const safeDecode = (value?: string | null): string => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const BacklinkMain = () => {
  const location = useLocation();
  const { hash, pathname } = location;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filters = useSelector(state => state.filters);
  const { domain_slug: paramDomainSlug, domain: paramDomain } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (paramDomainSlug && paramDomain) {
      dispatch(
        setDomain({
          slug: paramDomainSlug,
          name: paramDomain
        })
      );
    }

    // Drop legacy ?u=&m= once path already carries domain_slug/domain.
    if (!paramDomainSlug || !paramDomain) return;
    if (!searchParams.has('u') && !searchParams.has('m')) return;
    const params = new URLSearchParams(searchParams);
    params.delete('u');
    params.delete('m');
    setSearchParams(params, { replace: true });
  }, [dispatch, paramDomain, paramDomainSlug, searchParams, setSearchParams]);
  useEffect(() => {
    const filterParamsFromUrl = Array.from(
      new URLSearchParams(location.search).entries()
    )
      .filter(([key]) => key.includes('FBy'))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    const expectedParamsFromStore = convertToQueryString(filters);

    console.log('Filter Check URL:', safeDecode(filterParamsFromUrl));
    console.log('Filter Check Store:', safeDecode(expectedParamsFromStore));

    if (
      filters &&
      location.search.includes('FBy') &&
      !_.isEqual(
        safeDecode(filterParamsFromUrl),
        safeDecode(expectedParamsFromStore)
      )
    ) {
      const queryObjects = parseQueryString(location.search);
      dispatch(setFilters(queryObjects));
    }

    setTimeout(() => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }, 0);
  }, [location.search]);

  const { domain, domain_slug } = useDomain();
  const access = useToolAccess('backlink');
  const accessError = access.error as unknown as ApiQueryError;
  const activeDomainSlug = paramDomainSlug || domain_slug;
  const activeDomain = paramDomain || domain;
  const [breadcrumbData, setBreadcrumbData] = useState([]);
  const isGapPage = pathname.includes('/gap');
  const isBacklinkIndex =
    pathname === '/backlink' || pathname === '/backlink/';
  const hasBacklinkTarget =
    isGapPage || (activeDomainSlug && activeDomain);

  // Legacy /backlink?u=&m= → overview with domain path when possible.
  useEffect(() => {
    const hasLegacyQuery = searchParams.has('u') || searchParams.has('m');
    if (!isBacklinkIndex || isGapPage || !hasLegacyQuery) return;
    if (activeDomainSlug && activeDomain) {
      navigate(
        `/backlink/overview/${activeDomainSlug}/${activeDomain}`,
        { replace: true }
      );
    }
  }, [
    isBacklinkIndex,
    isGapPage,
    navigate,
    activeDomainSlug,
    activeDomain,
    searchParams
  ]);

  const {
    url: statusUrl,
    domain_slug: statusDomainSlug
  } = useBacklinkQueryBody();
  const skipStatusQuery =
    isBacklinkIndex || isGapPage || !statusDomainSlug || !access.haveAccess;
  const [statusPollMs, setStatusPollMs] = useState(8000);
  const {
    currentData: pipelineStatus,
    isLoading: isPipelineLoading,
    isFetching: isPipelineFetching,
    isError: isPipelineError,
    error: pipelineError,
    refetch: refetchPipelineStatus
  } = useGetDomainStatusQuery(
    {
      domain_slug: statusDomainSlug,
      ...(statusUrl ? { url: statusUrl } : {})
    },
    {
      skip: skipStatusQuery,
      pollingInterval: skipStatusQuery ? 0 : statusPollMs
    }
  );

  useEffect(() => {
    if (
      pipelineStatus?.status === 'ready' ||
      pipelineStatus?.status === 'not_registered' ||
      pipelineStatus?.status === 'needs_analyze'
    ) {
      setStatusPollMs(0);
    } else {
      setStatusPollMs(8000);
    }
  }, [pipelineStatus?.status]);
  const pipelineReady = pipelineStatus?.status === 'ready';
  const waitingForThisSlug = !skipStatusQuery && !pipelineStatus;
  const showPipelineError = shouldShowDataLoadError(
    isPipelineError,
    pipelineError
  );
  const isRouteActive = (route: { to: string }, navLinkActive = false) => {
    if (navLinkActive) return true;
    if (pathname === route.to || pathname.startsWith(`${route.to}/`)) {
      return true;
    }
    if (activeDomainSlug && activeDomain) {
      const linkPath = `${route.to}/${activeDomainSlug}/${activeDomain}`;
      return pathname === linkPath || pathname.startsWith(`${linkPath}/`);
    }
    return false;
  };
  if (access.isLoading) return <></>;

  return (
    <ExpireCheck>
      {!access.haveAccess ? (
        <ToolAccessDenied access={access} tool_label="ابزار بک لینک" />
      ) : shouldShowDataLoadError(access.isError, accessError) ? (
        <DataLoadError
          onRetry={() => access.refetch()}
          isRetrying={access.isFetching}
          isNetworkError={isNetworkApiError(accessError)}
        />
      ) : (
    <>
      {!isBacklinkIndex && (
        <>
          <ToolsBreadcrumb
            parentName="ابزار بک‌لینک"
            parentUrl="/backlink"
            childs={breadcrumbData}
          />
          <ToolTabs
            items={backlinkTabRoutes.children.map(route => {
              const isDisabled = route.disabled === true;
              return {
                key: route.to,
                label: route.name,
                to: isDisabled
                  ? '#'
                  : buildBacklinkUrl(
                      route.to,
                      activeDomainSlug,
                      activeDomain,
                      searchParams
                    ),
                disabled: isDisabled,
                state: { path: location.pathname, query: null },
                active: !isDisabled && isRouteActive(route)
              };
            })}
          />
        </>
      )}

      {isBacklinkIndex ? (
        <Outlet context={[breadcrumbData, setBreadcrumbData]} />
      ) : (
        <>
        {hasBacklinkTarget ? (
          showPipelineError ? (
            <DataLoadError
              error={pipelineError}
              onRetry={() => refetchPipelineStatus()}
              isRetrying={isPipelineFetching}
              isNetworkError={isNetworkApiError(pipelineError)}
            />
          ) : isGapPage || pipelineReady ? (
            <Outlet context={[breadcrumbData, setBreadcrumbData]} />
          ) : (
            <BacklinkPipelineState
              status={pipelineStatus?.status}
              message={pipelineStatus?.message}
              taskId={pipelineStatus?.task_id}
              progressPercentage={pipelineStatus?.progress_percentage}
              urlsDone={pipelineStatus?.urls_done}
              urlsTotal={pipelineStatus?.urls_total}
              url={statusUrl}
              domain_slug={statusDomainSlug}
              isLoading={isPipelineLoading || waitingForThisSlug}
              onAnalyzed={() => setStatusPollMs(8000)}
            />
          )
        ) : (
          <NoDataBacklink />
        )}
        {hasBacklinkTarget && !showPipelineError && pipelineReady && (
          <DataSourceNotice />
        )}
        </>
      )}
    </>
      )}
    </ExpireCheck>
  );
};

export default BacklinkMain;
